import React, { FC, ButtonHTMLAttributes, ReactNode, forwardRef, AnchorHTMLAttributes } from "react";

import classnames from "classnames";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { withStyleProps } from "Utils/styleProps";

import Icon from "Common/Icon";

import type { LinkProps } from "react-router-dom";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  color?: "primary" | "secondary" | "tertiary" | "white" | "gray" | "ghost" | "link" | "info" | "destructive";
  fullWidth?: boolean;
  size?: "xs" | "small" | "medium" | "large" | "destructive";
  round?: boolean;
  circle?: boolean;
  submitting?: boolean;
  type?: "button" | "reset" | "submit";
  transform?: "none";
  children?: ReactNode;
}

const baseClasses = "si-btn h-[unset] min-h-[unset] border-none outline-2 -outline-offset-2";

const colorClasses = {
  primary: "si-btn-primary",
  secondary: "si-btn-neutral border border-solid border-neutral-300",
  tertiary: "si-btn-link no-underline hover:text-blue-600 active:text-blue-600",
  white: "si-btn-neutral text-gray-900 bg-white hover:bg-neutral-100 active:bg-neutral-200",
  gray: "si-btn-neutral shadow-button",
  ghost: "si-btn-ghost",
  link: "si-btn-link hover:text-blue-600 active:text-blue-600",
  info: "si-btn-info shadow-button",
  destructive: "si-btn-neutral bg-white hover:bg-gray-100 shadow-button no-underline text-error-900 text-error-900",
};

const sizeClasses = {
  xs: "text-xs font-normal py-1 px-1",
  small: "text-sm font-normal py-1 px-4",
  destructive: "text-sm font-normal py-1 px-[9px]", // This is to give the x a symmetrical overall width and height
  medium: "text-base py-[6px] px-4",
  large: "text-lg py-4 px-5",
};

const roundClasses = "rounded-3xl";
const circleClasses = "rounded-full";
const RouterLink = Link as React.ElementType;

/**
  Cobalt Button - Based on DaisyUI button classes
  @see https://daisyui.com/components/button/
  */
export const UndecoratedButton: FC<ButtonProps> = ({
  children,
  className,
  color = "primary",
  round = false,
  circle = false,
  fullWidth,
  size = "small",
  disabled,
  type = "button",
  submitting = false,
  transform,
  ...rest
}) => {
  const classes = twMerge(
    baseClasses,
    classnames(sizeClasses[size], colorClasses[color], {
      "w-full": fullWidth,
      "shadow-none": disabled,
      [roundClasses]: round,
      [circleClasses]: circle,
      [`si-btn-transform-${transform}`]: transform,
    }),
    className,
  );

  return (
    // eslint-disable-next-line react/button-has-type
    <button type={type} className={classes} disabled={disabled} {...rest}>
      {submitting && <Icon icon="spinner-third" flavor="solid" spacing="right" spin />}
      {children}
    </button>
  );
};

export interface UndecoratedLinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  className?: string;
  color?: "primary" | "secondary" | "tertiary" | "white" | "gray" | "link" | "info" | "destructive";
  round?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium" | "large";
  children?: ReactNode;
  navigate?: () => void;
  transform?: string;
}

const linkColorClasses = {
  primary: "hover:text-white focus:text-white",
  secondary: "hover:text-blue focus:text-blue",
  tertiary: "hover:text-blue-600 active:text-blue-600",
  white: "hover:text-gray-900 focus:text-gray-900",
  gray: "hover:text-gray-900 focus:text-gray-900",
  link: "hover:text-blue-600 active:text-blue-600",
  info: "si-btn-info shadow-button",
  destructive: "hover:text-error-900 focus:text-error-900",
};

export interface RouterButtonProps extends Omit<LinkProps, "className" | "color"> {
  className?: string;
  color?: "primary" | "secondary" | "tertiary" | "white" | "gray" | "ghost" | "link" | "info" | "destructive";
  fullWidth?: boolean;
  size?: "xs" | "small" | "medium" | "large" | "destructive";
  round?: boolean;
  submitting?: boolean;
  disabled?: boolean;
  transform?: string;
  children?: ReactNode;
}

export const UndecoratedRouterButton = forwardRef<HTMLAnchorElement, RouterButtonProps>(
  (
    { children, className, color = "primary", round = false, fullWidth, size = "small", disabled, submitting = false, onClick, tabIndex, transform, ...props },
    ref,
  ) => {
    const classes = twMerge(
      baseClasses,
      "hover:no-underline focus:no-underline",
      classnames(sizeClasses[size], colorClasses[color], linkColorClasses[color], {
        "w-full": fullWidth,
        "shadow-none": disabled,
        [roundClasses]: round,
        [`si-btn-transform-${transform}`]: transform,
      }),
      className,
    );

    return React.createElement(
      RouterLink,
      {
        ref,
        ...props,
        "aria-disabled": disabled || undefined,
        className: classes,
        onClick: (event) => {
          if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          onClick?.(event);
        },
        tabIndex: disabled ? -1 : tabIndex,
      },
      <>
        {submitting && <Icon icon="spinner-third" flavor="solid" spacing="right" spin />}
        {children}
      </>,
    );
  },
);

const UndecoratedLinkButton = forwardRef<HTMLAnchorElement, UndecoratedLinkButtonProps>(
  ({ navigate, className, color = "primary", size = "small", round, fullWidth, onClick, children, transform, ...props }, ref) => {
    const classes = twMerge(
      baseClasses,
      "hover:no-underline focus:no-underline",
      classnames(sizeClasses[size], colorClasses[color], linkColorClasses[color], {
        "w-full": fullWidth,
        [roundClasses]: round,
        [`si-btn-transform-${transform}`]: transform,
      }),
      className,
    );

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <a
        ref={ref}
        {...props}
        onClick={(e) => {
          if (navigate) e.preventDefault();
          if (onClick) onClick(e);
          if (navigate) navigate();
        }}
        className={classes}
      >
        {children}
      </a>
    );
  },
);

export const LinkButton = withStyleProps(UndecoratedLinkButton);
export const RouterButton = withStyleProps(UndecoratedRouterButton);
export default withStyleProps(UndecoratedButton);
